<template>
  <ValidationProvider :name="`${attribute}-${uuid}`" :rules="!rules ? model.rules[attribute] : rules" v-slot="v"
                      tag="div" :vid="vid" :customMessages="model.customMessages[attribute]" ref="currentObserver">
    <b-form-group
      v-if="isInput() || isTextarea() || isCkeditor() || isTags() || isRadioGroup() || isDatepicker() || isTimepicker() || isDateRangePicker() || isTreePicker()"
      :class="{'has-error': getState(v) === false, 'has-success': getState(v) === true}"
    >
      <label v-if="computedLabel">
        {{ computedLabel }}
        <span v-if="v.required" class="text-danger">*</span>
        <field-title-hint v-if="computedTitleHint" :target="`${attribute}-${vid}`" :title="computedTitleHint"/>
      </label>
      <b-input-group v-if="isInput()" :prepend="prepend" :append="append" :size="size">
        <template v-slot:append v-if="appendQuestion">
          <b-tooltip :target="`question-mark-tooltip-${attribute}-${uuid}`" :title="appendQuestion"/>
          <b-input-group-text :id="`question-mark-tooltip-${attribute}-${uuid}`" class="hover-cursor">
            <font-awesome-icon icon="question-circle" class="icon"/>
          </b-input-group-text>
        </template>
        <b-form-input v-if="type === 'number'" ref="currentInput" :size="size" :type="type" :disabled="disabled"
                      :readonly="readonly" :autofocus="autofocus" :name="`${attribute}-${uuid}`" @keyup="onKeyup"
                      :key="`${attribute}-${uuid}`" :id="inputId" v-model="model[attribute]" @change="onChange"
                      @input="onInput" @keydown="onKeydown" @blur="onBlur" :state="getState(v)"
                      :placeholder="computedPlaceholder" :min="min" :step="step"/>
        <b-form-input v-else ref="currentInput" :size="size" :type="type" :disabled="disabled" :readonly="readonly"
                      :autofocus="autofocus" :name="`${attribute}-${uuid}`" :key="`${attribute}-${uuid}`"
                      :id="inputId" v-model="model[attribute]" @change="onChange" @input="onInput" @keydown="onKeydown"
                      @keyup="onKeyup" @blur="onBlur" @mousedown="onMousedown" :state="getState(v)"
                      :placeholder="computedPlaceholder"/>
      </b-input-group>
      <b-form-textarea v-if="isTextarea()" ref="currentInput" :type="type" :name="`${attribute}-${uuid}`"
                       :key="`${attribute}-${uuid}`" v-model="model[attribute]" :state="getState(v)"
                       :placeholder="computedPlaceholder" :rows="rows" :disabled="disabled"/>
      <ckeditor v-if="isCkeditor()" :editor="ckeditorConfig.editor" ref="currentInput" :disabled="disabled"
                tag-name="div"
                :name="`${attribute}-${uuid}`" :key="`${attribute}-${uuid}`" :state="getState(v)" @ready="onReady"
                :class="{'is-invalid': getState(v), 'ckeditor-disabled': disabled}" v-model="model[attribute]"
                :placeholder="computedPlaceholder"
                :config="ckeditorConfig.editorConfig" @input="onInput"/>
      <b-form-tags v-if="isTags()" :state="getState(v)" :key="`${attribute}-${uuid}`" :name="`${attribute}-${uuid}`"
                   add-button-variant="primary" v-model="model[attribute]" remove-on-delete ref="currentInput"
                   :tag-validator="computedTagValidator" :invalid-tag-text="invalidTagText"
                   :placeholder="computedPlaceholder" @input="onChange"/>
      <b-form-radio-group v-if="isRadioGroup()" :id="`radio-group-${attribute}`" v-model="model[attribute]"
                          :options="radioGroupOptions" :state="getState(v)" :name="`${attribute}-${uuid}`"
                          :key="`${attribute}-${uuid}`"/>
      <b-form-input v-if="isDatepicker()" v-model="model[attribute]" ref="currentInput" :disabled="disabled"
                    :dateFormatOptions="{ year: 'numeric', month: 'long', day: 'numeric' }" :state="getState(v)"
                    :name="`${attribute}-${uuid}`" :key="`${attribute}-${uuid}`" type="date" :min="min" :max="max"
                    :placeholder="computedPlaceholder" :size="size" @change="onChange" @blur="onBlur"/>
      <b-form-input v-if="isTimepicker()" v-model="model[attribute]" :disabled="disabled" ref="currentInput"
                    :hour12="false" :name="`${attribute}-${uuid}`" :key="`${attribute}-${uuid}`" :state="getState(v)"
                    type="time" :placeholder="computedPlaceholder" :size="size" @change="onChange"/>
      <treeselect v-if="isTreePicker()" v-model="model[attribute]" :options="items" @select="onTreepickerSelect"
                  @deselect="onTreepickerDeselect"
                  :normalizer="treePickerOptions.normalizer" :placeholder="placeholder" :max-height="200"
                  :multiple="multiple" :disabled="disabled" :flat="treePickerOptions.flat"
                  :value-consists-of="treePickerValueConsistsOf"/>
      <template v-if="isDateRangePicker()">
        <b-form-input type="text" v-model="model[attribute]" v-show="false"/>
        <artisio-date-ranger-picker :attribute="attribute" :model="model" :state="getState(v)" :disabled="disabled"
                                    :placeholder="computedPlaceholder"/>
      </template>
      <b-form-invalid-feedback :state="getState(v)">
        {{ getError(v.errors) }}
      </b-form-invalid-feedback>
      <small class="text-success" v-if="computedInfoText">{{ computedInfoText }}</small>
      <b-form-text v-if="charCounter && model[attribute]">
        {{ $t(`Character Count: {length}`, {length: getCharacterLength(model[attribute])}) }}
      </b-form-text>
      <b-form-text v-if="computedHint">
        {{ computedHint }}
      </b-form-text>
    </b-form-group>

    <b-form-group v-else-if="isCheckbox()"
                  :class="{'has-error': getState(v) === false, 'has-success': getState(v) === true}">
      <b-form-checkbox ref="currentInput" :disabled="disabled" :name="`${attribute}-${uuid}`" @change="onChange"
                       :key="`${attribute}-${uuid}`" v-model="model[attribute]" :switch="isSwitch" :size="size"
                       :state="getState(v)">
        <span v-if="computedLabel">
          {{ computedLabel }}
          <span v-if="v.required" class="text-danger">*</span>
          <field-title-hint v-if="computedTitleHint" :target="`${attribute}-${vid}`" :title="computedTitleHint"/>
        </span>
      </b-form-checkbox>
      <b-form-invalid-feedback :state="getState(v)">{{ getError(v.errors) }}
      </b-form-invalid-feedback>
      <small class="text-success" v-if="computedInfoText">{{ computedInfoText }}</small>
      <b-form-text v-if="computedHint">
        {{ computedHint }}
      </b-form-text>
    </b-form-group>

    <b-form-group v-else-if="isSelect2()"
                  :class="{'has-error': getState(v) === false, 'has-success': getState(v) === true}">
      <label v-if="computedLabel">
        {{ computedLabel }}
        <span v-if="v.required" class="text-danger">*</span>
        <field-title-hint v-if="computedTitleHint" :target="`${attribute}-${vid}`" :title="computedTitleHint"/>
      </label>
      <template v-if="appendQuestion">
        <b-input-group class="d-flex flex-nowrap">
          <template v-if="appendQuestion" v-slot:append>
            <b-tooltip :target="`question-mark-tooltip-${attribute}-${uuid}`" :title="appendQuestion"/>
            <b-input-group-text :id="`question-mark-tooltip-${attribute}-${uuid}`" class="hover-cursor">
              <font-awesome-icon icon="question-circle" class="icon"/>
            </b-input-group-text>
          </template>
          <select2 :items="items" :options="options" :value-field="valueField" :display-field="displayField"
                   :tags="tags" :display-field-for-selection="displayFieldForSelection"
                   :disabled="disabled" :search-handler="searchHandler" :name="`${attribute}-${uuid}`"
                   :multiple="multiple"
                   :key="`${attribute}-${uuid}`" v-model="model[attribute]" @select="onSelect2Select"
                   @unselect="onSelect2Unselect" @clear="onSelect2Clear"
                   ref="currentInput"/>
        </b-input-group>
      </template>
      <template v-else>
        <select2 :items="items" :options="options" :value-field="valueField" :display-field="displayField" :tags="tags"
                 :disabled="disabled" :search-handler="searchHandler" :name="`${attribute}-${uuid}`"
                 :multiple="multiple" :display-field-for-selection="displayFieldForSelection"
                 :key="`${attribute}-${uuid}`" v-model="model[attribute]" @select="onSelect2Select"
                 @unselect="onSelect2Unselect" @clear="onSelect2Clear"
                 ref="currentInput"/>
      </template>
      <b-form-invalid-feedback :state="getState(v)">
        {{ getError(v.errors) }}
      </b-form-invalid-feedback>
      <small class="text-success" v-if="computedInfoText">{{ computedInfoText }}</small>
      <b-form-text v-if="computedHint">
        {{ computedHint }}
      </b-form-text>
    </b-form-group>
  </ValidationProvider>
</template>

<script>

  import BaseModel from "./BaseModel";
  import Select2 from "../select2/Select2";
  import {uuid} from 'vue-uuid';
  import InlineEditor from '@ckeditor/ckeditor5-build-inline';
  import ArtisioDateRangerPicker from "@/core/components/input-widget/ArtisioDateRangerPicker";
  import Treeselect from '@riophae/vue-treeselect'
  import '@riophae/vue-treeselect/dist/vue-treeselect.css'
  import FieldTitleHint from "../../../modules/inventory/components/FieldTitleHint";
  import CustomerService from "@/modules/customer/CustomerService";

  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  export default {
    name: 'InputWidget',
    components: {FieldTitleHint, ArtisioDateRangerPicker, Select2, Treeselect},
    props: {
      model: BaseModel,
      attribute: String,
      label: {
        type: [String, Boolean],
        default: null
      },
      type: {
        type: String,
        default: 'text'
      },
      placeholder: {
        type: [String, Boolean],
        default: null
      },
      disabled: {
        type: Boolean,
        default: false
      },
      readonly: {
        type: Boolean,
        default: false
      },

      /* Select 2 Props start */
      items: {
        type: [Object, Array],
        default: () => {
          return {}
        }
      },
      options: {
        type: Object,
        default: () => {
          return {};
        }
      },
      valueField: {
        type: String,
        default: 'uuid'
      },
      displayField: {
        type: [String, Function],
        default: 'text'
      },
      displayFieldForSelection: {
        type: [String, Function],
        default: null
      },
      searchHandler: {
        type: Function,
        default: null
      },
      radioGroupOptions: {
        type: Array,
        default: () => []
      },
      isSwitch: {
        type: Boolean,
        default: false
      },
      tags: {
        type: [Boolean, Array],
        default: false
      },
      multiple: {
        type: Boolean,
        default: false
      },
      size: {
        type: String,
        default: 'md'
      },
      hint: {
        type: [Boolean, String],
        default: false
      },
      infoText: {
        type: String,
        default: null
      },
      prepend: {
        type: String,
        default: ''
      },
      append: {
        type: String,
        default: ''
      },
      autofocus: {
        type: Boolean,
        default: false,
      },
      rules: {
        type: [String, Object],
        default: null,
        required: false
      },
      inputId: {
        type: String,
        default() {
          return `${this.attribute}-${uuid.v4()}`
        }
      },
      appendQuestion: {
        type: String,
        default: null
      },
      vid: {
        type: String,
        default() {
          return `vid-${this.attribute}-${uuid.v4()}`
        }
      },
      min: {
        type: [String, Number],
        default: 0
      },
      max: {
        type: [String, Number],
        default: Infinity
      },
      step: {
        type: [String, Number],
        default: 0
      },
      charCounter: {
        type: Boolean,
        default: false,
      },
      treePickerOptions: {
        type: Object,
        default() {
          return {
            normalizer: (node) => node,
            flat: false
          }
        }
      },
      titleHint: {
        type: [Boolean, String],
        default: false
      },
      ckEditor: {
        type: Object,
        default: () => {
        }
      },
      rows: {
        type: Number,
        default: null
      },
      tagValidator: {
        type: Function,
        default: null
      },
      invalidTagText: {
        type: String,
        default: null
      }
    },
    data() {
      return {
        uuid: uuid.v4(),
      }
    },
    methods: {
      getError(errors) {
        if (this.model.hasError(this.attribute)) {
          return this.model.getFirstError(this.attribute);
        }
        return errors[0];
      },
      getState(validator) {
        if (this.model.hasError(this.attribute)) {
          return false;
        }

        // Validate is set when validate function of vee validate is run
        if (validator.validated === true) {
          // return validator.valid;
          return validator.valid === true ? null : false;
        }

        if (!validator.touched) {
          return null;
        }

        // return !validator.touched ? null : validator.valid
        return validator.valid === true ? null : false;
      },
      focus() {
        this.$refs.currentInput.focus();
      },
      isInput() {
        return ['text', 'number', 'date', 'datetime-local', 'password', 'email', 'search', 'url', 'tel', 'time', 'range', 'color'].includes(this.type)
      },
      isTextarea() {
        return this.type === 'textarea';
      },
      isCheckbox() {
        return this.type === 'checkbox';
      },
      isSelect2() {
        return this.type === 'select2';
      },
      isSearchSelect() {
        return this.type === 'search-select';
      },
      isRadioGroup() {
        return this.type === 'radio-group';
      },
      isTags() {
        return this.type === 'tags';
      },
      isCkeditor() {
        return this.type === 'ckeditor';
      },
      isDatepicker() {
        return this.type === 'datepicker';
      },
      isDateRangePicker() {
        return this.type === 'daterangepicker';
      },
      isTimepicker() {
        return this.type === 'timepicker';
      },
      isTreePicker() {
        return this.type === 'treepicker';
      },
      onTreepickerSelect(val) {
        this.$emit('onTreepickerSelect', val)
      },
      onTreepickerDeselect(node) {
        if (!node) {
          return;
        }
        const raw = Object.prototype.hasOwnProperty.call(node, 'raw') ? node.raw : null;
        if (raw && raw.uuid) {
          this.$emit('select2Unselect', raw);
          return;
        }
        const id = node.id != null ? node.id : node.uuid;
        this.$emit('select2Unselect', {uuid: id});
      },
      async onChange(val) {
        if (this.type === 'number' && val === '') {
          this.model[this.attribute] = null;
        }
        if ((this.type === 'date' || this.type === 'datepicker') && val === '') {
          val = null;
          this.model[this.attribute] = null;
        }
        if (this.attribute === "iban") {
          let response = await CustomerService.getSwiftFromIban({iban: val});
          if (response.success) {
            if (response.body['swift_code']) {
              return this.model['swift'] = response.body['swift_code']
            }
            return this.model['swift'] = ""
          }
        }
        this.$emit('change', val);
      },
      onInput($event) {
        this.$emit('input', $event);
      },
      onBlur($event) {
        if (this.type === 'number' && $event.target.value === '') {
          this.model[this.attribute] = null;
        }
        if ((this.type === 'date' || this.type === 'datepicker') && $event.target.value === '') {
          $event.target.value = null;
          this.model[this.attribute] = null;
        }
        this.$emit('blur', $event);
      },
      onKeydown($event) {
        this.$emit('keydown', $event);
      },
      onKeyup($event) {
        this.$emit('keyup', $event);
      },
      onMousedown($event) {
        this.$emit('mousedown', $event);
      },
      onSelect2Select(data) {
        this.$emit('select2Select', data);
        // Following sample of code is temporary solution, select2 select is fired earlier than data change,
        // therefore, when rules are dynamically changed because of data the rules are still not affected
        setTimeout(async () => {
          if (this.$refs.currentObserver) {
            await this.$refs.currentObserver.validate()
          }
        }, 100)
      },
      onSelect2Clear(data) {
        this.$emit('select2Clear', data);
      },
      onSelect2Unselect(data) {
        this.$emit('select2Unselect', data);
      },
      onReady(data) {
        this.$emit('onCkeditorReady', data)
      },
      getCharacterLength(value) {
        return this.$options.filters.striptags(value).length;
      },
    },
    computed: {
      ckeditorConfig() {
        return {
          ...getCkeditorDefaultConfig(),
          ...this.ckEditor
        }
      },
      computedPlaceholder() {
        if (this.placeholder === false) {
          return '';
        } else if (this.placeholder === true) {
          return this.model.getAttributePlaceholder(this.attribute)
        } else {
          return this.placeholder;
        }
      },
      computedLabel() {
        if (this.label === null) {
          return this.model.getAttributeLabel(this.attribute)
        }
        return this.label;
      },
      computedHint() {
        if (!this.hint || typeof this.hint === 'string') {
          return this.hint;
        }
        return this.model.getAttributeHint(this.attribute)
      },
      computedTitleHint() {
        if (this.titleHint === false) {
          return '';
        }
        return this.model.getAttributeTitleHint(this.attribute)
      },
      computedInfoText() {
        return this.infoText;
      },
      computedTagValidator() {
        // b-form-tags calls the validator for every tag; fall back to a no-op
        // (always valid) when no validator is provided so it never crashes.
        return this.tagValidator || (() => true);
      },
      treePickerValueConsistsOf() {
        const v = this.treePickerOptions && this.treePickerOptions.valueConsistsOf;
        if (v !== undefined && v !== null) {
          return v;
        }
        return 'BRANCH_PRIORITY';
      },
    }
  };

  function getCkeditorDefaultConfig() {
    return {
      editor: InlineEditor,
      editorData: '',
      editorConfig: {
        toolbar: ['bold', 'italic', 'heading', 'link', 'bulletedList', 'numberedList', 'indent', 'outdent', 'blockQuote']
      }
    };
  }

</script>

<style lang="scss">
  @import "../../../core/scss/variables";

  .vue-treeselect__control {
    height: 34px !important;
    font-size: 14px !important;
    color: #999 !important;
    border: 1px solid #ccc !important;
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);

    .has-error & {
      border-color: $danger !important;
    }
  }

  .vue-treeselect__placeholder {
    color: #999 !important;
  }

  .vue-treeselect__x, .vue-treeselect__control-arrow {
    width: 7px !important;
    color: #999 !important;
  }

  .ck-editor__editable {
    max-height: 400px;
  }

  .ck-rounded-corners .ck.ck-balloon-panel, .ck.ck-balloon-panel.ck-rounded-corners {
    z-index: 10055 !important;
  }

  .ck-editor__editable_inline {
    border: 1px solid #ced4da !important;
    border-radius: 0.25rem !important;

    .has-error & {
      border-color: $danger !important;
    }
  }

  .ck-editor__editable_inline.ck-focused {
    outline: 0 !important;
    border-color: #79d4fd !important;
    box-shadow: 0 0 0 0.2rem rgba(3, 169, 244, 0.25) !important;
  }

  .ckeditor-disabled {
    background-color: $input-disabled-bg;
  }

  .form-control.ckeditor-disabled {
    background: $input-disabled-bg;
    cursor: not-allowed !important;
  }

</style>
